import React, { useState, useEffect } from 'react';
import apiClient from '../utils/apiClient';
import countryData from "./countryData";
import { createAgent, searchUsersForAgent } from "../utils/APIRoutes";
import './AgentRegistration.css';

const AgentRegistration = ({ onAgentCreated }) => {
  const [formData, setFormData] = useState({
    fullName: '',
    userId: '',
    sex: 'Male',
    age: '',
    phoneNumber: '',
    nationalId: '',
    email: '',
    location: '',
    country: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [countrySearch, setCountrySearch] = useState('');
  const [countryResults, setCountryResults] = useState([]);
  const [showCountryResults, setShowCountryResults] = useState(false);


const validateForm = () => {
  if (!formData.fullName.trim()) {
    return 'Full name is required.';
  }

  if (!formData.userId.trim()) {
    return 'Please select a user first.';
  }

  if (!formData.country.trim()) {
    return 'Country is required.';
  }

  if (!formData.sex) {
    return 'Sex is required.';
  }

  const age = Number(formData.age);

  if (!formData.age || !Number.isInteger(age)) {
    return 'Please enter a valid age.';
  }

  if (age < 18) {
    return 'Agent must be at least 18 years old.';
  }

  if (age > 100) {
    return 'Age must be reasonable.';
  }

  const phone = formData.phoneNumber.replace(/\D/g, '');

  if (!/^[0-9]{10,15}$/.test(phone)) {
    return 'Please enter a valid phone number with 10–15 digits.';
  }

  if (!formData.nationalId.trim()) {
    return 'National ID is required.';
  }

  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
    return 'Please enter a valid email address.';
  }

  if (!formData.location.trim()) {
    return 'Location is required.';
  }

  return null;
};

  useEffect(() => {
    const trimmed = searchTerm.trim();

    if (trimmed.length < 2) {
      setSearchResults([]);
      setSearchLoading(false);
      return;
    }

    setSearchLoading(true);
    const debounce = setTimeout(async () => {
      try {
        const { data } = await apiClient.get(
          `${searchUsersForAgent}?q=${encodeURIComponent(trimmed)}`
        );
        setSearchResults(data.users || []);
      } catch (err) {
        console.error('User search failed:', err);
        setSearchResults([]);
      } finally {
        setSearchLoading(false);
      }
    }, 400);

    return () => clearTimeout(debounce);
  }, [searchTerm]);

  // Filter countries based on search term
  useEffect(() => {
    if (countrySearch.trim() === '') {
      setCountryResults([]);
      return;
    }

    const results = countryData.filter(country =>
      country.name.toLowerCase().includes(countrySearch.toLowerCase())
    ).slice(0, 10); // Limit to 10 results for performance

    setCountryResults(results);
  }, [countrySearch]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleContactSelect = (contact) => {
    setFormData(prev => ({
      ...prev,
      userId: contact._id,
      email: contact.email,
      fullName: contact.username
    }));
    setSearchTerm('');
    setShowResults(false);
  };

  const handleCountrySelect = (country) => {
    setFormData(prev => ({
      ...prev,
      country: country.name
    }));
    setCountrySearch(country.name);
    setShowCountryResults(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationError = validateForm();

      if (validationError) {
        setError(validationError);
        return;
      }
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // SECURITY: swapped `axios` for `apiClient` — createAgent is
      // admin-only server-side now (manage_agents), so it needs the
      // session cookie to succeed.
      await apiClient.post(createAgent, formData);
      setSuccess('Agent registered successfully!');
      setFormData({
        fullName: '',
        userId: '',
        sex: 'Male',
        age: '',
        phoneNumber: '',
        nationalId: '',
        email: '',
        location: '',
        country: ''
      });
      setCountrySearch('');
      onAgentCreated();
    } catch (err) {
      setError(err.response?.data?.msg || 'Failed to register agent');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="agent-registration">
      <h3>Register New Agent</h3>
      {error && <div className="alert error">{error}</div>}
      {success && <div className="alert success">{success}</div>}
      
      <form onSubmit={handleSubmit}>
        {/* Contact Search */}
        <div className="form-group">
          <label>Search User</label>
          <div className="contact-search">
            <input
              type="text"
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setShowResults(true);
              }}
              onFocus={() => setShowResults(true)}
            />
            {showResults && searchLoading && (
              <div className="contact-results">
                <div className="contact-item">Searching...</div>
              </div>
            )}
            {showResults && !searchLoading && searchResults.length > 0 && (
              <div className="contact-results">
                {searchResults.map(contact => (
                  <div 
                    key={contact._id} 
                    className="contact-item"
                    onClick={() => handleContactSelect(contact)}
                  >
                    <div className="contact-name">{contact.username}</div>
                    <div className="contact-email">{contact.email}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="form-group">
          <label>User ID</label>
          <input
            type="text"
            name="userId"
            value={formData.userId}
            onChange={handleChange}
            required
            readOnly
          />
        </div>

        <div className="form-group">
          <label>Full Name</label>
          <input
            type="text"
            name="fullName"
            value={formData.fullName}
            onChange={handleChange}
            required
          />
        </div>

        <div className="form-group">
          <label>Email</label>
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
          />
        </div>

        {/* Country Search */}
        <div className="form-group">
          <label>Country</label>
          <div className="country-search">
            <input
              type="text"
              name="countrySearch"
              placeholder="Search for a country..."
              value={countrySearch}
              onChange={(e) => {
                setCountrySearch(e.target.value);
                setShowCountryResults(true);
              }}
              onFocus={() => setShowCountryResults(true)}
            />
            {showCountryResults && countryResults.length > 0 && (
              <div className="country-results">
                {countryResults.map(country => (
                  <div 
                    key={`${country.name}-${country.phoneCode}`} 
                    className="country-item"
                    onClick={() => handleCountrySelect(country)}
                  >
                    <div className="country-name">
                      {country.name}
                      {country.flag && <span className="country-flag">{country.flag}</span>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <input
            type="hidden"
            name="country"
            value={formData.country}
          />
        </div>

        <div className="form-group">
          <label>Sex</label>
          <select
            name="sex"
            value={formData.sex}
            onChange={handleChange}
            required
          >
            <option value="Male">Male</option>
            <option value="Female">Female</option>
            <option value="Other">Other</option>
          </select>
        </div>

        <div className="form-group">
          <label>Age</label>
          <input
            type="number"
            name="age"
            value={formData.age}
            onChange={handleChange}
            min="18"
            max="100"
            required
          />
        </div>

        <div className="form-group">
          <label>Phone Number</label>
          <input
            type="tel"
            name="phoneNumber"
            value={formData.phoneNumber}
            onChange={handleChange}
            required
          />
        </div>

        <div className="form-group">
          <label>National ID</label>
          <input
            type="text"
            name="nationalId"
            value={formData.nationalId}
            onChange={handleChange}
            required
          />
        </div>

        <div className="form-group">
          <label>Location</label>
          <input
            type="text"
            name="location"
            value={formData.location}
            onChange={handleChange}
            required
          />
        </div>

        <button type="submit" disabled={loading}>
          {loading ? 'Registering...' : 'Register Agent'}
        </button>
      </form>
    </div>
  );
};

export default AgentRegistration;
